// src/RoomAvailability.js - VERSIONE UNIFICATA

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, getDocs, getDoc, doc, query, where, addDoc } from 'firebase/firestore';
import moment from 'moment';
import Select from 'react-select';
import DatePicker, { registerLocale } from "react-datepicker";
import it from 'date-fns/locale/it';
import './RoomAvailability.css';
import { AssignmentModal } from './AssignmentModal';
import { toast } from 'react-toastify';
import { useUser } from './UserContext';

registerLocale('it', it);

// Componente per visualizzare i risultati delle assegnazioni (Slot Occupati)
const AssignmentsResultsTable = ({ assignments }) => (
    <div className="assignment-table-wrapper">
        <table className="assignment-results-table">
            <thead>
                <tr>
                    <th>Data</th>
                    <th>Orario</th>
                    <th>Stanza</th>
                    <th>Medico</th>
                    <th>Specialistiche</th>
                    <th>Ricorrenza</th>
                </tr>
            </thead>
            <tbody>
                {assignments.map(a => (
                    // La key deve includere la data per distinguere le ricorrenze
                    <tr key={a.id + a.date.format('YYYY-MM-DD')}>
                        <td>{a.date.format('ddd DD/MM/YYYY')}</td>
                        <td>{a.startTime} - {a.endTime}</td>
                        <td>{a.roomName}</td>
                        <td>{a.doctorName}</td>
                        <td>
                            {a.specialtyNames.map((name, i) => (
                                <span key={i} className="tag-pill assignment-tag">{name}</span>
                            ))}
                        </td>
                        <td>
                            {a.isRecurring ? `Ogni ${a.recurrenceInterval} sett.` : 'Singola'}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);


function RoomAvailability() {
    // Stati per i dati di base
    const { userProfile } = useUser();
    const [rooms, setRooms] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [specialties, setSpecialties] = useState([]); // Nuovo stato
    const [businessHours, setBusinessHours] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // Stati per i filtri
    const [searchMode, setSearchMode] = useState('availability'); // 'availability' o 'assignment'
    const [selectedRooms, setSelectedRooms] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(moment().add(1, 'month').toDate());
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(null);
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('20:00');
    const [minDuration, setMinDuration] = useState(60);
    // Nuovi filtri assegnazioni
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);

    // Stato per i risultati
    const [foundSlots, setFoundSlots] = useState({}); // Slot liberi
    const [foundAssignments, setFoundAssignments] = useState([]); // Slot occupati
    const [isSearching, setIsSearching] = useState(false);

    // Stati per il modale
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [assignmentModalData, setAssignmentModalData] = useState({});

    // Opzioni per i Select
    const doctorOptions = doctors.map(d => ({ value: d.id, label: `${d.firstName} ${d.lastName}` }));
    const specialtyOptions = specialties.map(s => ({ value: s.value, label: s.label }));


    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            // --- INIZIO MODIFICA ---
            if (!userProfile || !userProfile.centerId) {
                 setIsLoading(false);
                 setRooms([]);
                 return;
            }
            const centerId = userProfile.centerId;

            // Recupero di tutte le collezioni nidificate e le impostazioni
            const [roomsSnap, doctorsSnap, hoursSnap, specialtiesSnap] = await Promise.all([ 
                getDocs(collection(db, 'centers', centerId, "rooms")), // <-- MODIFICA PERCORSO
                getDocs(collection(db, 'centers', centerId, "doctors")), // <-- MODIFICA PERCORSO
                getDoc(doc(db, 'centers', centerId, "settings", "businessHours")), // <-- MODIFICA PERCORSO
                getDocs(collection(db, 'centers', centerId, "specialties")) // <-- MODIFICA PERCORSO
            ]);
            // --- FINE MODIFICA ---

            const roomsList = roomsSnap.docs.map(d => ({ value: d.id, label: d.data().name, ...d.data() })).sort((a,b) => a.number - b.number);
            const specialtiesList = specialtiesSnap.docs.map(d => ({ id: d.id, name: d.data().name }));
            const doctorsList = doctorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            setRooms(roomsList);
            setDoctors(doctorsList);
            setSpecialties(specialtiesList.map(s => ({ value: s.id, label: s.name })));
            setBusinessHours(hoursSnap.exists() ? hoursSnap.data() : {});
            setIsLoading(false);
        };
        fetchData();
    }, [userProfile]); // <-- MODIFICA QUI

    // LOGICA PER LA RICERCA DISPONIBILITÀ (Slot Liberi)
    const searchAvailability = async () => {
        const assignmentsQuery = query(
        collection(db, 'centers', userProfile.centerId, "roomAssignments"), // <-- MODIFICA PERCORSO
            where("validUntil", ">=", moment(startDate).format('YYYY-MM-DD')),
        );
        const assignmentsSnap = await getDocs(assignmentsQuery);
        const relevantAssignments = assignmentsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(a => a.validUntil >= moment(startDate).format('YYYY-MM-DD'));
        
        const availableSlots = [];
        let currentDate = moment(startDate).startOf('day');
        const roomsToSearch = selectedRooms.length > 0 ? selectedRooms : rooms;

        while (currentDate.isSameOrBefore(moment(endDate).endOf('day'))) {
            const dayOfWeek = currentDate.isoWeekday();
            
            if (selectedDayOfWeek && dayOfWeek !== selectedDayOfWeek.value) {
                currentDate.add(1, 'day');
                continue;
            }

            const dayHours = businessHours[dayOfWeek];
            if (!dayHours) {
                currentDate.add(1, 'day');
                continue;
            }

            for (const room of roomsToSearch) {
                const dayAssignments = relevantAssignments.filter(a => 
                    a.roomId === room.value &&
                    moment(currentDate).isBetween(a.validFrom, a.validUntil, 'day', '[]') &&
                    (a.daysOfWeek || []).includes(dayOfWeek) &&
                    !(a.excludedDates || []).includes(currentDate.format('YYYY-MM-DD'))
                ).sort((a, b) => a.startTime.localeCompare(b.startTime));

                let lastEndTime = dayHours.open;
                
                dayAssignments.forEach(assign => {
                    if (assign.startTime > lastEndTime) {
                        availableSlots.push({ room, from: lastEndTime, to: assign.startTime, date: currentDate.clone() });
                    }
                    lastEndTime = assign.endTime > lastEndTime ? assign.endTime : lastEndTime;
                });

                if (dayHours.close > lastEndTime) {
                    availableSlots.push({ room, from: lastEndTime, to: dayHours.close, date: currentDate.clone() });
                }
            }
            currentDate.add(1, 'day');
        }
        
        const finalFilteredSlots = availableSlots.filter(slot => {
            const slotStart = moment(slot.from, 'HH:mm');
            const slotEnd = moment(slot.to, 'HH:mm');
            const searchStart = moment(startTime, 'HH:mm');
            const searchEnd = moment(endTime, 'HH:mm');
            
            const overlapStart = moment.max(slotStart, searchStart);
            const overlapEnd = moment.min(slotEnd, searchEnd);
            
            const duration = overlapEnd.diff(overlapStart, 'minutes');
            
            return duration >= minDuration;
        });

        const slotsByRoom = {};
        finalFilteredSlots.forEach(slot => {
            if (!slotsByRoom[slot.room.value]) {
                slotsByRoom[slot.room.value] = {
                    roomName: slot.room.label,
                    slots: []
                };
            }
            slotsByRoom[slot.room.value].slots.push(slot);
        });

        for (const roomId in slotsByRoom) {
            slotsByRoom[roomId].slots.sort((a, b) => {
                if (a.date.isBefore(b.date, 'day')) return -1;
                if (a.date.isAfter(b.date, 'day')) return 1;
                return a.from.localeCompare(b.from);
            });
        }

        setFoundSlots(slotsByRoom);
    };

    // LOGICA PER LA RICERCA ASSEGNAZIONI (Slot Occupati)
    const searchAssignments = async () => {
    const assignmentsQuery = query(
        collection(db, 'centers', userProfile.centerId, "roomAssignments"), // <-- MODIFICA PERCORSO
            where("validUntil", ">=", moment(startDate).format('YYYY-MM-DD')),
        );
        const assignmentsSnap = await getDocs(assignmentsQuery);
        
        const roomsToSearchIds = selectedRooms.length > 0 ? selectedRooms.map(r => r.value) : rooms.map(r => r.value);
        
        const filteredAssignments = assignmentsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(a => a.validFrom <= moment(endDate).format('YYYY-MM-DD'));

        const finalAssignments = [];

        for (let current = moment(startDate).startOf('day'); current.isSameOrBefore(moment(endDate).endOf('day')); current.add(1, 'day')) {
            const dayOfWeek = current.isoWeekday();
            const dayStr = current.format('YYYY-MM-DD');

            if (selectedDayOfWeek && dayOfWeek !== selectedDayOfWeek.value) continue;

            filteredAssignments.forEach(a => {
                // Filtri di Tempo (sovrapposizione, solo se l'assegnazione è tra gli orari di ricerca)
                if (a.startTime > endTime || a.endTime < startTime) return;

                // Filtri di Data, Ricorrenza e Stanza
                if (!roomsToSearchIds.includes(a.roomId)) return;
                if (!(a.daysOfWeek || []).includes(dayOfWeek)) return;
                if ((a.excludedDates || []).includes(dayStr)) return;
                if (moment(current).isBefore(a.validFrom, 'day') || moment(current).isAfter(a.validUntil, 'day')) return;
                if (a.isRecurring) {
                    const startDateMoment = moment(a.validFrom).startOf('isoWeek');
                    const weeksDifference = current.diff(startDateMoment, 'weeks');
                    if (weeksDifference > 0 && weeksDifference % a.recurrenceInterval !== 0) return;
                }
                
                // Filtri di Medico/Specialistica
                const doctor = doctors.find(d => d.id === a.doctorId);
                if (selectedDoctor && a.doctorId !== selectedDoctor.value) return;
                if (selectedSpecialty && !(doctor?.specialtyIds || []).includes(selectedSpecialty.value)) return;

                // Aggiungi l'assegnazione arricchita ai risultati
                const room = rooms.find(r => r.value === a.roomId);
                
                finalAssignments.push({
                    ...a,
                    date: current.clone(),
                    roomName: room?.label,
                    doctorName: doctor ? `${doctor.firstName} ${doctor.lastName}` : 'N/A',
                    specialtyNames: doctor?.specialtyIds?.map(id => specialties.find(s => s.value === id)?.label).filter(Boolean) || []
                });
            });
        }

        // Ordina e imposta lo stato
        finalAssignments.sort((a, b) => {
            if (a.date.isBefore(b.date, 'day')) return -1;
            if (a.date.isAfter(b.date, 'day')) return 1;
            return a.startTime.localeCompare(b.startTime);
        });

        setFoundAssignments(finalAssignments);
    };


    const handleSearch = async () => {
        if (moment(startDate).isAfter(moment(endDate))) {
            alert("La data di fine non può precedere la data di inizio.");
            return;
        }

        setIsSearching(true);
        setFoundSlots({});
        setFoundAssignments([]); 

        if (searchMode === 'availability') { 
            await searchAvailability();
        } else {
            await searchAssignments();
        }
        
        setIsSearching(false);
    };

    const handleSlotClick = (slot) => {
        setAssignmentModalData({
            day: slot.date,
            preselectedRoomId: slot.room.value,
            preselectedStartTime: slot.from,
            preselectedEndTime: slot.to,
        });
        setIsAssignmentModalOpen(true);
    };

    const handleSaveAssignment = async (data) => {
        const assignmentData = {
            roomId: data.roomId,
            doctorId: data.doctorId,
            startTime: data.startTime,
            endTime: data.endTime,
            daysOfWeek: data.daysOfWeek,
            validFrom: data.validFrom,
            validUntil: data.validUntil,
        isRecurring: data.isRecurring,
        recurrenceInterval: data.isRecurring ? Number(data.recurrenceInterval) : 1,
        collaboratorIds: data.collaboratorIds || [],
        centerId: userProfile.centerId, // <-- AGGIUNGI QUESTA RIGA
    };
    await addDoc(collection(db, 'centers', userProfile.centerId, "roomAssignments"), assignmentData); // <-- MODIFICA PERCORSO
    setIsAssignmentModalOpen(false);
        toast.success("Assegnazione creata con successo!");
        handleSearch();
    };

    const dayOfWeekOptions = [
        { value: 1, label: 'Lunedì' }, { value: 2, label: 'Martedì' },
        { value: 3, label: 'Mercoledì' }, { value: 4, label: 'Giovedì' },
        { value: 5, label: 'Venerdì' }, { value: 6, label: 'Sabato' }
    ];

    if (isLoading) return <p>Caricamento dati...</p>;

    return (
        <>
            {isAssignmentModalOpen && (
                <AssignmentModal
                    modalData={assignmentModalData}
                    rooms={rooms}
                    doctors={doctors}
                    onSave={handleSaveAssignment}
                    onClose={() => setIsAssignmentModalOpen(false)}
                    
                />
            )}
            <div className="availability-finder-container">
                <div className="filters-panel">
                    {/* SELETTORE DI MODALITÀ */}
                    <div className="mode-selector-group">
                        <button 
                            onClick={() => setSearchMode('availability')} 
                            className={searchMode === 'availability' ? 'mode-button active' : 'mode-button'}
                        >
                            Cerca Disponibilità
                        </button>
                        <button 
                            onClick={() => setSearchMode('assignment')} 
                            className={searchMode === 'assignment' ? 'mode-button active' : 'mode-button'}
                        >
                            Cerca Assegnazioni
                        </button>
                    </div>
                    
                    <h4>Filtri di Ricerca</h4>
                    <div className="form-group">
                        <label>Stanza/e (opzionale)</label>
                        <Select isMulti options={rooms} value={selectedRooms} onChange={setSelectedRooms} placeholder="Tutte le stanze"/>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Cerca dal</label>
                            <DatePicker selected={startDate} onChange={date => setStartDate(date)} dateFormat="dd/MM/yyyy" locale="it" />
                        </div>
                        <div className="form-group">
                            <label>Cerca al</label>
                            <DatePicker selected={endDate} onChange={date => setEndDate(date)} dateFormat="dd/MM/yyyy" locale="it" />
                        </div>
                    </div>
                    
                    {/* FILTRI SPECIFICI PER ASSEGNAZIONI */}
                    {searchMode === 'assignment' && (
                        <>
                            <div className="form-group">
                                <label>Medico</label>
                                <Select isClearable options={doctorOptions} value={selectedDoctor} onChange={setSelectedDoctor} placeholder="Tutti i medici"/>
                            </div>
                            <div className="form-group">
                                <label>Specialistica</label>
                                <Select isClearable options={specialtyOptions} value={selectedSpecialty} onChange={setSelectedSpecialty} placeholder="Tutte le specialistiche"/>
                            </div>
                        </>
                    )}
                    
                    {/* DURATA MINIMA SLOT (Solo per disponibilità) */}
                    {searchMode === 'availability' && (
                        <div className="form-group">
                            <label>Durata minima slot libero (in minuti)</label>
                            <input type="number" value={minDuration} onChange={e => setMinDuration(Number(e.target.value))} min="1" placeholder="Es. 60" />
                        </div>
                    )}

                    <div className="form-group">
                        <label>Giorno della settimana (opzionale)</label>
                        <Select options={dayOfWeekOptions} isClearable value={selectedDayOfWeek} onChange={setSelectedDayOfWeek} placeholder="Tutti i giorni"/>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Dalle ore</label>
                            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Alle ore</label>
                            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                        </div>
                    </div>

                    <button onClick={handleSearch} disabled={isSearching} className="search-button">
                        {isSearching ? 'Ricerca in corso...' : searchMode === 'availability' ? 'Cerca Disponibilità' : 'Cerca Assegnazioni'}
                    </button>
                </div>

                <div className="results-panel">
                    <h4>Risultati ({searchMode === 'availability' ? 'Slot Liberi' : 'Assegnazioni'})</h4>
                    
                    {isSearching ? <p>Ricerca in corso...</p> : (
                        searchMode === 'availability' ? (
                            // RISULTATI DISPONIBILITÀ (Slot Liberi)
                            Object.keys(foundSlots).length > 0 ? (
                                <div className="slots-grid-container">
                                    {Object.entries(foundSlots).map(([roomId, roomData]) => (
                                        <div key={roomId} className="room-column-results">
                                            <div className="room-column-header">{roomData.roomName}</div>
                                            <div className="slots-list-column">
                                                {roomData.slots.map((slot, index) => (
                                                    <div key={index} className="slot-card" onClick={() => handleSlotClick(slot)}>
                                                        <div className="slot-card-header">
                                                            <span className="slot-date">{slot.date.format('ddd DD/MM/YY')}</span>
                                                        </div>
                                                        <div className="slot-card-body">
                                                            <span className="slot-time">{slot.from} - {slot.to}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-results">Nessuno slot libero trovato con i criteri specificati.</p>
                            )
                        ) : (
                            // RISULTATI ASSEGNAZIONI (Slot Occupati/Assegnati)
                            foundAssignments.length > 0 ? (
                                <AssignmentsResultsTable assignments={foundAssignments} />
                            ) : (
                                <p className="no-results">Nessuna assegnazione trovata con i criteri specificati.</p>
                            )
                        )
                    )}
                </div>
            </div>
        </>
    );
}

export default RoomAvailability;